import React, { useState, useRef, Suspense } from 'react';
import GlobalToast from '../GlobalToast';
import ConnectionStatus from '../ConnectionStatus';
import { Outlet, useLocation, matchPath } from 'react-router-dom';
import styled, { css } from 'styled-components';
import Sidebar from './Sidebar';
import Header from './Header';
import RightPanel from './RightPanel';
import WishlistRightPanel from './WishlistRightPanel';
import DetailRightPanel from './DetailRightPanel';
import ShelfRightPanel from './ShelfRightPanel';
import BottomNavigation from './BottomNavigation';
import { useWishlistFilters } from '../../context/WishlistFilterContext';
import { useShelfTab } from '../../context/ShelfTabContext';
import { useLayout } from '../../context/LayoutContext';
import { useUser } from '../../context/UserContext';
import useBackLayer from '../../hooks/useBackLayer';
import useScrollMemory from '../../hooks/useScrollMemory';
import AppLoader from '../AppLoader';
import { PAGE_GUTTERS } from '../../styles/PageLayout';

const LayoutContainer = styled.div`
  display: flex;
  width: 100%;
  min-height: var(--polka-viewport-height, 100vh);
  padding-top: var(--polka-safe-top, 0px);
  padding-left: var(--polka-safe-left, 0px);
  padding-right: var(--polka-safe-right, 0px);
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background};
  max-width: 100vw;

  html[data-telegram='true'] & {
    height: var(--polka-visible-height, var(--polka-viewport-height, 100dvh));
    min-height: 0;
    overscroll-behavior: none;
  }

  /* В «новой» вёрстке ленты (как VK) сдвигаем всю компоновку
     (сайдбар + лента + правая панель) от краёв экрана к центру. */
  ${({ $isNewFeedLayout }) =>
    $isNewFeedLayout &&
    css`
      height: var(--polka-viewport-height, 100vh);
      --vk-offset: 350px;

      @media (max-width: 1900px) {
        --vk-offset: 220px;
      }

      @media (max-width: 1600px) {
        --vk-offset: 130px;
      }

      @media (max-width: 1400px) {
        --vk-offset: 80px;
      }

      @media (max-width: 1200px) {
        --vk-offset: 40px;
      }

      @media (max-width: 1024px) {
        --vk-offset: 0px;
      }
    `}
`;

const MainWrapper = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  margin-left: ${({ $isNewFeedLayout }) =>
    $isNewFeedLayout ? 'calc(var(--vk-offset, 0px) + 280px)' : '280px'};
  margin-right: ${({ $isDetailPage, $isNewFeedLayout }) => {
    if ($isNewFeedLayout) return 'calc(var(--vk-offset, 0px) + 320px)';
    return $isDetailPage ? '0' : '320px';
  }};
  display: flex;
  flex-direction: column;
  max-width: 100%;

  @media (max-width: 1200px) {
    margin-right: ${({ $isDetailPage, $isNewFeedLayout }) => {
      if ($isNewFeedLayout) return 'calc(var(--vk-offset, 0px) + 280px)';
      return $isDetailPage ? '0' : '280px';
    }};
  }

  @media (max-width: 1024px) {
    margin-right: 0;
    margin-left: 80px;
  }

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const MainContent = styled.main`
  --page-gutter: ${({ $isNewFeedLayout }) => $isNewFeedLayout ? PAGE_GUTTERS.compact : PAGE_GUTTERS.classic}px;
  flex: 1;
  min-width: 0;
  width: 100%;
  padding: ${({ $isDetailPage, $isNewFeedLayout }) =>
    $isDetailPage ? '0' : ($isNewFeedLayout ? '6px var(--page-gutter)' : '24px var(--page-gutter)')};
  margin-top: ${({ $hasHeader }) => ($hasHeader ? '64px' : '0')};
  background: ${({ theme }) => theme.colors.background};
  min-height: ${({ $hasHeader }) => ($hasHeader ? 'calc(100vh - 64px)' : '100vh')};
  transition: background-color 0.3s ease;
  max-width: 100%;
  box-sizing: border-box;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    --page-gutter: ${PAGE_GUTTERS.mobile}px;
    padding: ${({ $isDetailPage }) => ($isDetailPage ? '0' : '6px var(--page-gutter)')};
    padding-bottom: calc(${({ $isDetailPage }) => ($isDetailPage ? '70px' : '76px')} + var(--polka-safe-bottom, 0px));
    margin-top: ${({ $hasHeader }) => ($hasHeader ? '64px' : '0')};
    min-height: 100vh;
    width: 100%;
    max-width: 100%;
  }

  html[data-telegram='true'] & {
    min-height: 0;
    margin-top: 0;
    overscroll-behavior-y: none;
  }
`;

const SidebarOverlay = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }
`;

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const filters = useWishlistFilters();
  const { activeTab: shelfTab } = useShelfTab();
  const { newFeedLayout } = useLayout();
  const mainRef = useRef(null);
  const { user } = useUser();
  useScrollMemory(mainRef, location.pathname + location.search, user?.id);
  useBackLayer(() => setSidebarOpen(false), sidebarOpen);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const isDetailPage = Boolean(matchPath({ path: '/item/:category/:id', end: false }, location.pathname));
  const isFandomPage = Boolean(matchPath({ path: '/fandom/:slug', end: true }, location.pathname));
  const isFullPage = isDetailPage || isFandomPage;
  
  const isShelfPage = location.pathname === '/shelf';

  // Every page with the standard header and sidebars uses the selected layout,
  // including nested collection, catalog and legal pages.
  const isNewFeedLayout = newFeedLayout && !isFullPage;

  return (
    <LayoutContainer $isNewFeedLayout={isNewFeedLayout}>
      <Sidebar className={sidebarOpen ? 'open' : ''} />
      <SidebarOverlay $isOpen={sidebarOpen} onClick={closeSidebar} />
      
      <MainWrapper $isDetailPage={isFullPage} $isNewFeedLayout={isNewFeedLayout}>
        {!isFullPage && <Header onMenuClick={toggleSidebar} />}
        <MainContent ref={mainRef} $hasHeader={!isFullPage} $isDetailPage={isFullPage} $isNewFeedLayout={isNewFeedLayout}>
          <ConnectionStatus /><Suspense fallback={<AppLoader />}><Outlet /></Suspense>
        </MainContent>
      </MainWrapper>

      {isFullPage
        ? isDetailPage ? <DetailRightPanel /> : null
        : location.pathname === '/wishlist'
          ? <WishlistRightPanel {...filters} />
          : isShelfPage && shelfTab === 'shelf'
            ? <ShelfRightPanel />
            : <RightPanel />}
      <GlobalToast />
      <BottomNavigation />
    </LayoutContainer>
  );
};

export default Layout;
